import React, { useCallback, useEffect, useRef, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Typography from "@mui/material/Typography";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { extendPamSession } from "../../../api/users";
import { get_jwt_token } from "../../../api/helper_funtions";
import { showSuccess, showWarning } from "../../../utils/showMessage";
import { logoutUser } from "../../../state/slices/user";
import { isRTL } from "../../../i18n/config";
import CrossButton from "../ButtonSpinner/CrossButton";
import CustomModal from "../CustomModal/CustomModal";
import GeneralButton from "../SaveButton/GeneralButton";
import "./PamSessionWarning.scss";
import { PAM_SESSION_WARNING_THRESHOLD_SECONDS as WARNING_THRESHOLD_SECONDS } from "../../../utils/generic_constant";

function computeSecondsUntilJwtExpiry(token) {
  if (!token || typeof token !== "string") return null;
  const segments = token.split(".");
  if (segments.length < 2) return null;
  try {
    let b64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const { exp } = JSON.parse(atob(b64));
    if (typeof exp !== "number") return null;
    return Math.max(0, Math.floor(exp - Date.now() / 1000));
  } catch {
    return null;
  }
}

function formatTime(seconds) {
  if (seconds === null || seconds < 0) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

const PamSessionWarning = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtlMode = isRTL(i18n.language);
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [expired, setExpired] = useState(false);

  const openRef = useRef(false);
  const dismissedRef = useRef(false);
  const countdownTimerRef = useRef(null);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const stopCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const closeModal = useCallback(() => {
    stopCountdown();
    setOpen(false);
    openRef.current = false;
    setCountdown(null);
    setExpired(false);
  }, [stopCountdown]);

  const startCountdown = useCallback(() => {
    stopCountdown();
    countdownTimerRef.current = setInterval(() => {
      const fresh = computeSecondsUntilJwtExpiry(get_jwt_token());
      setCountdown(fresh !== null ? fresh : (prev) => Math.max(0, (prev || 0) - 1));
      if (fresh !== null && fresh <= 0) {
        stopCountdown();
        setExpired(true);
      }
    }, 1000);
  }, [stopCountdown]);

  useEffect(() => {
    const checkTimer = setInterval(() => {
      const secondsRemaining = computeSecondsUntilJwtExpiry(get_jwt_token());

      if (secondsRemaining === null || secondsRemaining <= 0) {
        clearInterval(checkTimer);
        return;
      }

      if (secondsRemaining > WARNING_THRESHOLD_SECONDS) {
        dismissedRef.current = false;
      }

      if (
        secondsRemaining <= WARNING_THRESHOLD_SECONDS &&
        !dismissedRef.current &&
        !openRef.current
      ) {
        setCountdown(secondsRemaining);
        setOpen(true);
        openRef.current = true;
        startCountdown();
        triggerBrowserNotification();
      }
    }, 1000);

    return () => {
      clearInterval(checkTimer);
      stopCountdown();
    };
  }, [startCountdown, stopCountdown]);

  const triggerBrowserNotification = useCallback(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification("PAM Session Expiring Soon", {
        body: "Your privileged access session is about to end. Click to extend.",
        icon: document.getElementById("favicon").href,
      });
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          new Notification("PAM Session Expiring Soon", {
            body: "Your privileged access session is about to end. Click to extend.",
            icon: document.getElementById("favicon").href,
          });
        }
      });
    }
  }, []);

  const handleExtend = useCallback(async () => {
    const { data, error } = await extendPamSession();
    if (data !== null) {
      if (data.access_token) {
        localStorage.setItem("jwt", data.access_token);
      }
      dismissedRef.current = false;
      closeModal();
      showSuccess("Session extended.");
    }
    if (error !== null) {
      closeModal();
      if (error === 401 || error === 403) {
        showWarning("Your PAM session has already ended. Please sign in again.");
      } else {
        showWarning("Could not extend PAM session. Please sign in again.");
      }
    }
  }, [closeModal]);

  const handleDismiss = useCallback(() => {
    dismissedRef.current = true;
    closeModal();
  }, [closeModal]);

  const handleLoginAgain = useCallback(() => {
    closeModal();
    dispatch(logoutUser());
  }, [closeModal, dispatch]);

  return (
    <CustomModal
      open={open}
      width="420px"
      padding="0"
      customStyles={{ borderRadius: "12px", overflow: "hidden" }}
    >
      <div dir={isRtlMode ? "rtl" : "ltr"}>
        {expired ? (
          <>
            <div className="pam-session-header pam-session-header--expired">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="pam-session-icon-circle pam-session-icon-circle--expired">
                    <ErrorOutlineIcon className="pam-session-icon pam-session-icon--expired" />
                  </div>
                  <div>
                    <Typography className="pam-session-title pam-session-title--expired">
                      {t("pamSessionWarning.expiredTitle")}
                    </Typography>
                    <Typography className="pam-session-subtitle pam-session-subtitle--expired">
                      {t("pamSessionWarning.expiredSubtitle")}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div className="pam-session-countdown-body text-center">
              <Typography className="pam-session-hint" style={{ marginTop: 0, fontSize: "0.88rem" }}>
                {t("pamSessionWarning.expiredMessage")}
              </Typography>
            </div>

            <div className="d-flex justify-content-end" style={{ padding: "16px 24px 24px" }}>
              <GeneralButton
                onClickEvent={handleLoginAgain}
                value={t("pamSessionWarning.loginAgain")}
                variant="contained"
                size="large"
              />
            </div>
          </>
        ) : (
          <>
            <div className="pam-session-header">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="pam-session-icon-circle">
                    <AccessTimeIcon className="pam-session-icon" />
                  </div>
                  <div>
                    <Typography className="pam-session-title">
                      {t("pamSessionWarning.title")}
                    </Typography>
                    <Typography className="pam-session-subtitle">
                      {t("pamSessionWarning.subtitle")}
                    </Typography>
                  </div>
                </div>
                <CrossButton onClick={handleDismiss} />
              </div>
            </div>

            <div className="pam-session-countdown-body text-center">
              <Typography className="pam-session-countdown-label">{t("pamSessionWarning.timeRemaining")}</Typography>
              <Typography className="pam-session-countdown">
                {formatTime(countdown)}
              </Typography>
              <Typography className="pam-session-hint">
                {t("pamSessionWarning.hint")}
              </Typography>
            </div>

            <div className="d-flex justify-content-end" style={{ padding: "16px 24px 24px" }}>
              <GeneralButton
                onClickEvent={handleExtend}
                value={t("pamSessionWarning.staySignedIn")}
                variant="contained"
                size="large"
              />
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default PamSessionWarning;
